import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingBag, Store, Bike, Users, FileClock,
  LogOut, Menu, X, Bell, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAdminAuth } from "@/lib/admin-auth-context";
import adminApi from "@/lib/admin-api";

const NAV = [
  { href: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { href: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { href: "/admin/restaurants", label: "Restaurants", icon: Store },
  { href: "/admin/riders", label: "Riders", icon: Bike },
  { href: "/admin/customers", label: "Customers", icon: Users },
  { href: "/admin/activity", label: "Activity", icon: FileClock },
];

export function AdminLayout({ children }) {
  const { admin, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const refreshNotifs = async () => {
    try {
      const { data } = await adminApi.get("/notifications");
      setNotifications(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refreshNotifs();
    const id = setInterval(refreshNotifs, 30000);
    return () => clearInterval(id);
  }, []);

  const unread = notifications.filter((n) => !n.is_read).length;

  const doLogout = () => { logout(); navigate("/admin/signin"); };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.href;
    return location.pathname.startsWith(item.href);
  };

  const SidebarContent = ({ onClick }) => (
    <>
      <div className="p-6">
        <Link to="/admin" onClick={onClick} className="flex items-center gap-2" data-testid="admin-nav-logo">
          <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="leading-tight">
            <p className="font-serif font-bold text-xl text-primary">Cravve</p>
            <p className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">Admin</p>
          </div>
        </Link>
      </div>
      <nav className="flex-1 px-3 flex flex-col gap-1 overflow-y-auto">
        {NAV.map((it) => (
          <Link
            key={it.href}
            to={it.href}
            onClick={onClick}
            data-testid={`admin-nav-${it.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-colors ${
              isActive(it)
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <it.icon size={18} />
            {it.label}
          </Link>
        ))}
      </nav>
      <div className="p-4 border-t">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">
            {(admin?.full_name || "A")[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{admin?.full_name}</p>
            <p className="text-[10px] text-primary uppercase tracking-wider font-semibold">
              {admin?.role?.replace("_", " ")}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" className="w-full gap-2 text-xs" onClick={doLogout} data-testid="admin-logout">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Mobile topbar */}
      <header className="md:hidden flex items-center justify-between p-4 bg-card border-b sticky top-0 z-40">
        <Link to="/admin" className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <span className="font-serif font-bold text-lg text-primary">Cravve Admin</span>
        </Link>
        <div className="flex items-center gap-2">
          <NotificationBell unread={unread} notifications={notifications} onRefresh={refreshNotifs} />
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)} data-testid="admin-mobile-menu">
            {mobileOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-[65px] bg-background z-30 border-t flex flex-col">
          <SidebarContent onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-card border-r fixed h-screen top-0 left-0 z-10">
        <SidebarContent />
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-60 flex flex-col w-full min-w-0">
        {/* Desktop topbar */}
        <header className="hidden md:flex h-14 border-b bg-card/80 backdrop-blur sticky top-0 z-20 items-center justify-end px-6">
          <NotificationBell unread={unread} notifications={notifications} onRefresh={refreshNotifs} />
        </header>
        <div className="flex-1 p-4 md:p-8">{children}</div>
      </main>
    </div>
  );
}

function NotificationBell({ unread, notifications, onRefresh }) {
  const markAllRead = async () => {
    await adminApi.post("/notifications/read-all");
    onRefresh();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="admin-notifications-btn">
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 bg-destructive text-destructive-foreground rounded-full text-[10px] font-bold flex items-center justify-center"
              data-testid="admin-notifications-count">
              {unread > 9 ? "9+" : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b">
          <p className="font-semibold text-sm">Notifications</p>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary font-semibold hover:underline"
              data-testid="admin-mark-all-read">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">No notifications yet.</p>
          )}
          {notifications.slice(0, 20).map((n) => (
            <div key={n.id} className={`p-3 border-b last:border-0 ${n.is_read ? "" : "bg-primary/5"}`}>
              <p className="text-sm font-medium leading-tight">{n.title}</p>
              {n.body && <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>}
              <p className="text-[10px] text-muted-foreground mt-1">
                {new Date(n.created_at).toLocaleString("en-GB", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
